import cn from 'classnames';
import Modal from '@components/Modal/Modal';
import Tabs from '@components/Tabs/Tabs';
import Loader from '@components/Loader/Loader';
import { Button, FCX, ID } from '@datassential/platform-ui-lib';
import {
    useFilters,
    SelectFiltersContext,
    FilterValues,
} from "@vms/SelectFilters/SelectFiltersContext";
import { useCallback, useEffect, useMemo, useState, FC, PropsWithChildren } from "react";
import { VenuesContextForm } from '@components/VenuesForm/VenuesForm';
import { MenuItemsContextForm } from '@components/MenuItems/MenuItems';
import { AttributesContextForm } from '@components/Attributes/Attributes';
import { TagsContextForm } from '@containers/FiltersContainer/Tags';
import { AdditionalFiltersContextForm } from '@containers/FiltersContainer/AdditionalFilters';

const FiltersTabs = {
    Venues: 1,
    Attributes: 2,
    MenuItems: 3,
    AdditionalFilters: 4,
    Tags: 5,
} as const;

type FilterTabId = typeof FiltersTabs[keyof typeof FiltersTabs];

interface FilterTab {
    id: FilterTabId;
    name: string;
}

const DraftFiltersProvider: FC<PropsWithChildren<{
    draftFilters: FilterValues;
    onDraftChange: (partial: Partial<FilterValues>) => void;
}>> = ({ draftFilters, onDraftChange, children }) => {
    const parentContext = useFilters();

    const draftContextValue = useMemo(
        () => ({
            ...parentContext,
            filters: draftFilters,
            setPartial: onDraftChange,
        }),
        [parentContext, draftFilters, onDraftChange]
    );

    return (
        <SelectFiltersContext.Provider value={draftContextValue}>
            {children}
        </SelectFiltersContext.Provider>
    );
};

const SelectFiltersModal: FCX = ({ className }) => {
    const {
        isFiltersPopupShown,
        isLoadingFilters,
        filters,
        closeFiltersPopup,
        tagCategories,
        setPartial,
    } = useFilters();


    const [draftFilters, setDraftFilters] = useState<FilterValues>(filters);

    const [selectedFilterTabId, setSelectedFilterTabId] = useState<ID>(FiltersTabs.Venues);

    useEffect(() => {
        if (isFiltersPopupShown) {
            setDraftFilters(filters);
            setSelectedFilterTabId(FiltersTabs.Venues);
        }
    }, [isFiltersPopupShown, filters]);

    const handleDraftChange = useCallback((partial: Partial<FilterValues>) => {
        setDraftFilters(prev => ({ ...prev, ...partial }));
    }, []);

    const cancel = useCallback(() => {
        closeFiltersPopup();
    }, [closeFiltersPopup]);

    const apply = useCallback(() => {
        setPartial(draftFilters);
        closeFiltersPopup();
    }, [draftFilters, setPartial, closeFiltersPopup]);


    const placeTabs = useMemo(
        () => [
            { id: FiltersTabs.Venues, name: 'Venues' },
            { id: FiltersTabs.Attributes, name: 'Attributes' },
        ],
        []
    );

    const foodTabs = useMemo(
        () => {
            const result: FilterTab[] = [
                { id: FiltersTabs.MenuItems, name: 'Item Types' },
            ];

            if (tagCategories.length > 0) {
                result.push({ id: FiltersTabs.Tags, name: 'AI Tags' });
            }

            result.push({ id: FiltersTabs.AdditionalFilters, name: 'Other' });
            return result;
        },
        [tagCategories]
    );

    return (
        <Modal closeModal={cancel} shown={isFiltersPopupShown} hasOverlay>
            <div className="Popup__wrapper">
                <div className={cn('Popup', 'Popup--large', 'SelectFiltersModal', className)}>
                    <div className="SelectFiltersModal__header">
                        <Tabs
                            title="Place"
                            data={placeTabs}
                            activeItemId={selectedFilterTabId}
                            handleClick={setSelectedFilterTabId}
                            className="is-place"
                            testId="selectFiltersPopupNavPlace"
                        />
                        <Tabs
                            title="Food"
                            data={foodTabs}
                            activeItemId={selectedFilterTabId}
                            handleClick={setSelectedFilterTabId}
                            className="is-food"
                            testId="selectFiltersPopupNavFood"
                        />
                        <div
                            className="SelectFiltersModal__tabs-tooltip SelectFiltersModal__description is-place"
                            data-testid="blockFiltersPopupNavigationTooltipPlace"
                        >
                            <div
                                className="SelectFiltersModal__tabs-tooltip-title"
                                data-testid="textFiltersPopupNavigationTooltipPlaceTitle"
                            >
                                <span>PLACE</span> filters let you filter on the <span>menus</span> to include in your search
                            </div>
                            <div data-testid="textFiltersPopupNavigationTooltipPlaceText-0">
                                For example, the "BBQ" cuisine filter will narrow your search to all BBQ restaurants.
                                Note: this won't include BBQ menu items at other types of places, like American or
                                Korean restaurants.
                            </div>
                        </div>
                        <div
                            className="SelectFiltersModal__tabs-tooltip SelectFiltersModal__description is-food"
                            data-testid="blockFiltersPopupNavigationTooltipFood"
                        >
                            <div
                                className="SelectFiltersModal__tabs-tooltip-title"
                                data-testid="textFiltersPopupNavigationTooltipFoodTitle"
                            >
                                <span>FOOD</span> filters let you filter on the <span>menu items</span> to include in your search
                            </div>
                            <div data-testid="textFiltersPopupNavigationTooltipFoodText-0">
                                For example, using the "Burger" item type will filter on all burgers, whether they
                                appear on an American, Steakhouse, or any other type of menu.
                            </div>
                        </div>
                    </div>

                    <div className="SelectFiltersModal__content">
                        <Loader isShown={isLoadingFilters} noBg/>
                        {!isLoadingFilters && (
                            <DraftFiltersProvider draftFilters={draftFilters} onDraftChange={handleDraftChange}>
                                {selectedFilterTabId === FiltersTabs.Venues && Object.keys(draftFilters.venues).length > 0 && (
                                    <VenuesContextForm/>
                                )}
                                {selectedFilterTabId === FiltersTabs.Attributes && Object.keys(draftFilters.attributes).length > 0 && (
                                    <AttributesContextForm/>
                                )}
                                {selectedFilterTabId === FiltersTabs.MenuItems && Object.keys(draftFilters.menuItems).length > 0 && (
                                    <MenuItemsContextForm/>
                                )}
                                {selectedFilterTabId === FiltersTabs.Tags && (
                                    <TagsContextForm/>
                                )}
                                {selectedFilterTabId === FiltersTabs.AdditionalFilters && draftFilters.menuItems.words && (
                                    <AdditionalFiltersContextForm/>
                                )}
                            </DraftFiltersProvider>
                        )}
                    </div>

                    <div className="SelectFiltersModal__footer">
                        <Button
                            className="SelectFiltersModal__footer-button"
                            modifiers={['naked']}
                            onClick={cancel}
                            data-testid="buttonFiltersPopupCancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="SelectFiltersModal__footer-button"
                            onClick={apply}
                            data-testid="buttonFiltersPopupApply"
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default SelectFiltersModal;
